import { z } from 'zod';

import { SubscriptionStatus } from 'src/constants';
import {
  LINE_ITEM_AMOUNT_ZERO,
  LineItem as InvoiceLineItem,
  oneOffLineItemSchema,
  paymentPreferencesSchema,
  productLineItemSchema,
} from 'src/legacy/components/billing/new/types';
import {
  LineItemType,
  MAX_LINE_ITEM_AMOUNT,
  MAX_TOTAL_AMOUNT,
  MAX_TOTAL_AMOUNT_ERROR,
  MIN_LINE_ITEM_AMOUNT,
  MIN_LINE_ITEM_AMOUNT_ERROR,
} from 'src/services/api/invoicesApi';
import { LineItemWithRate } from 'src/services/api/subscriptionsApi';
import { Invoice } from 'src/store/payments/types';

export enum SubscriptionCollectionMethod {
  ChargeAutomatically = 'charge_automatically',
  SendInvoice = 'send_invoice',
}

export enum SubscriptionInterval {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarterly = 'quarterly',
  Biannually = 'biannually',
  Year = 'year',
  SemiAnnually = 'semiannually',
}

export type LineItem = Omit<InvoiceLineItem, 'amount'> & { rate: number };

export type OneTimeLineItem = LineItem & {
  type: LineItemType.OneTime;
};

export type ProductLineItem = LineItem & {
  type: LineItemType.Product;
};

// Subscriptions do not allow having negative amounts
// So we're customising the amount field to not allow negative values
const amountFieldSchema = (currencyPrefix?: string) =>
  z
    .string({
      required_error: 'Price is required',
    })
    .refine(
      (value) => {
        const number = parseFloat(value);
        return number >= parseFloat(LINE_ITEM_AMOUNT_ZERO);
      },
      { message: 'Price cannot be negative' },
    )
    .refine(
      (value) => {
        const number = parseFloat(value);
        return number <= MAX_LINE_ITEM_AMOUNT;
      },
      {
        message: `Price can be at most ${currencyPrefix}${MAX_LINE_ITEM_AMOUNT}`,
      },
    );

const oneOffLineItemSchemaWithAmount = (currencyPrefix?: string) =>
  oneOffLineItemSchema(currencyPrefix).extend({
    amount: amountFieldSchema(currencyPrefix),
  });

const lineItemSchemaWithAmount = (currencyPrefix?: string) =>
  z
    .discriminatedUnion('type', [
      oneOffLineItemSchemaWithAmount(currencyPrefix),
      productLineItemSchema(currencyPrefix),
    ])
    .superRefine((data, ctx) => {
      if (
        data.type === LineItemType.OneTime &&
        parseFloat(data.amount) < MIN_LINE_ITEM_AMOUNT
      ) {
        ctx.addIssue({
          path: ['amount'],
          message: `Price must be between ${currencyPrefix}0.50 and ${currencyPrefix}100,000`,
          code: z.ZodIssueCode.custom,
        });
      }
    });

export const lineItemsWithAmountSchema = (currencyPrefix?: string) =>
  z.array(lineItemSchemaWithAmount(currencyPrefix));

const baseSubscriptionFieldsSchema = (currencyPrefix?: string) =>
  z.object({
    recipientId: z.string({ required_error: 'Please select a recipient' }),
    clientUserId: z.string().optional(),
    companyId: z.string().optional(),
    lineItems: lineItemsWithAmountSchema(currencyPrefix),
    paymentPreferences: paymentPreferencesSchema,
    taxPercentage: z
      .string()
      .optional()
      .refine(
        (value) => {
          // Tax is totally optional so we can return true if it's not provided
          if (!value) return true;
          const parsedQuantity = parseFloat(value);
          return parsedQuantity >= 0 && parsedQuantity <= 100;
        },
        { message: 'Tax rate must be under 100%' },
      ),
    totalAmount: z
      .number()
      .min(MIN_LINE_ITEM_AMOUNT, MIN_LINE_ITEM_AMOUNT_ERROR)
      .max(MAX_TOTAL_AMOUNT, MAX_TOTAL_AMOUNT_ERROR),
    attachment: z.string().nullable(),
    memo: z
      .string()
      .max(500, { message: 'Memo must be under 500 characters' })
      .optional(),
    currency: z.string().nullable(),
    prorate: z.boolean().optional(),
    resetBillingCycle: z.boolean().optional(),
    interval: z.enum([
      SubscriptionInterval.Day,
      SubscriptionInterval.Week,
      SubscriptionInterval.Month,
      SubscriptionInterval.Quarterly,
      SubscriptionInterval.Biannually,
      SubscriptionInterval.Year,
      SubscriptionInterval.SemiAnnually,
    ]),
    scheduleStartDate: z.number().optional(),
    scheduleEndDate: z.number().optional(),
    scheduleIterations: z.number().optional(),
    status: z.enum([
      SubscriptionStatus.Active,
      SubscriptionStatus.Unpaid,
      SubscriptionStatus.Incomplete,
      SubscriptionStatus.PastDue,
      SubscriptionStatus.Canceled,
      SubscriptionStatus.Trialing,
      SubscriptionStatus.IncompleteExpired,
      SubscriptionStatus.NotStarted,
      SubscriptionStatus.Paused,
    ]),
    created: z.number().optional(),
  });
const baseSubscriptionFieldsUnion = (currencyPrefix?: string) =>
  z.discriminatedUnion('collectionMethod', [
    baseSubscriptionFieldsSchema(currencyPrefix).extend({
      collectionMethod: z.literal(SubscriptionCollectionMethod.SendInvoice),
      dueDate: z.string(),
      dueDays: z
        .string({
          required_error:
            'Please enter the number of days till the payment is due',
        })
        .refine(
          (value) => {
            const parsedValue = parseFloat(value);
            return parsedValue > 0;
          },
          {
            message:
              'The number of days till the payment is due must be greater than 0',
          },
        )
        .refine(
          (value) => {
            const parsedValue = parseFloat(value);
            const DAYS_IN_TWO_YEARS = 365 * 2;
            return parsedValue <= DAYS_IN_TWO_YEARS;
          },
          {
            message: 'Due days cannot be more than 2 years in the future',
          },
        ),
    }),
    baseSubscriptionFieldsSchema(currencyPrefix).extend({
      collectionMethod: z.literal(
        SubscriptionCollectionMethod.ChargeAutomatically,
      ),
      paymentSourceId: z.string().nullable().optional(),
    }),
  ]);

export const subscriptionSchema = (currencyPrefix?: string) =>
  z.object({
    fields: baseSubscriptionFieldsUnion(currencyPrefix),
    paymentSourceId: z.string().nullable().optional(),
  });

export const subscriptionTemplateFieldSchema = (currencyPrefix?: string) =>
  z.object({
    lineItems: lineItemsWithAmountSchema(currencyPrefix),
    taxPercentage: z
      .string()
      .optional()
      .refine(
        (value) => {
          // Tax is totally optional so we can return true if it's not provided
          if (!value) return true;
          const parsedQuantity = parseFloat(value);
          return parsedQuantity >= 0 && parsedQuantity <= 100;
        },
        { message: 'Tax rate must be under 100%' },
      ),
    totalAmount: z
      .number()
      .min(MIN_LINE_ITEM_AMOUNT, MIN_LINE_ITEM_AMOUNT_ERROR)
      .max(MAX_TOTAL_AMOUNT, MAX_TOTAL_AMOUNT_ERROR),
    memo: z
      .string()
      .max(500, { message: 'Memo must be under 500 characters' })
      .optional(),
    attachment: z.string().nullable(),
    interval: z.enum([
      SubscriptionInterval.Day,
      SubscriptionInterval.Week,
      SubscriptionInterval.Month,
      SubscriptionInterval.Quarterly,
      SubscriptionInterval.Biannually,
      SubscriptionInterval.Year,
      SubscriptionInterval.SemiAnnually,
    ]),
  });

export const subscriptionTemplateSchema = (currencyPrefix?: string) =>
  z.object({
    fields: subscriptionTemplateFieldSchema(currencyPrefix),
    additionalFields: z.object({
      templateName: z.string({
        required_error: 'Template name is required',
      }),
    }),
  });

export type SubscriptionSchema = z.infer<ReturnType<typeof subscriptionSchema>>;
export type SubscriptionTemplateSchema = z.infer<
  ReturnType<typeof subscriptionTemplateSchema>
>;

export type SubscriptionFields = {
  recipientId?: string;
  clientUserId?: string;
  companyId?: string;
  memo: string;
  lineItems: LineItemWithRate[];
  interval: SubscriptionInterval;
  collectionMethod: string;
  daysUntilDue: number;
  taxPercentage: number;
  reActivatedBy: string;
  cancelledAt: string;
  scheduleEndDate: number;
  scheduleIterations: number;
  scheduleStartDate: number;
  allowPaymentViaCC: boolean;
  allowPaymentViaACH: boolean;
  absorbTransactionFees: boolean;
  currency: string;
  prorate: boolean;
  resetBillingCycle: boolean;
  attachment: string;
  paymentPreferences: SubscriptionSchema['fields']['paymentPreferences'];
  price?: number;
};

export type Subscription = {
  id: string;
  createdAt: string;
  fields: SubscriptionFields;
  additionalFields: {
    status: SubscriptionStatus;
    created: number;
    current_period_end: number;
    latest_invoice: Invoice;
  };
  paymentSourceId?: string | null;
};
